import { Query } from 'best-modules/utils'
import { sendRequest } from 'best-modules/vue-utils'
import { PaginationList } from 'best-modules/components/server-table/types'
import {
  Task,
  TaskControlUser,
  TaskCredentials,
  TaskObserverUser,
} from '@/utils/types/task'

type TaskPrintedFormType = 'incoming' | 'outgoing' | 'control' | 'observer'

type TaskPrintedFormResponse = {
  url: string
}

type TaskDashboardResponse = {
  stats: {
    overdue: number
    inWork: number
    waiting: number
    completed: number
  }
  nearestTasks: Task[]
}

const withAbsence = (
  url: string,
  absenceId?: number | string | null
): string => {
  return absenceId ? `${url}/${absenceId}` : url
}

const taskList = (query: Query): Promise<PaginationList<Task>> => {
  return sendRequest({
    name: 'taskList',
    method: 'get',
    url: {
      url: '/json/task',
      query,
    },
  })
}

const taskIncomingList = (query: Query): Promise<PaginationList<Task>> => {
  return sendRequest({
    name: 'taskIncomingList',
    method: 'get',
    url: {
      url: '/json/task/incomingTasks',
      query,
    },
  })
}

const taskAbsenceIncomingList = (
  absenceId: number | string,
  query: Query
): Promise<PaginationList<Task>> => {
  return sendRequest({
    name: 'taskAbsenceIncomingList',
    method: 'get',
    url: {
      url: `/json/task/incomingTasks/${absenceId}`,
      query,
    },
  })
}

const taskOutgoingList = (query: Query): Promise<PaginationList<Task>> => {
  return sendRequest({
    name: 'taskOutgoingList',
    method: 'get',
    url: {
      url: '/json/task/outgoingTasks',
      query,
    },
  })
}

const taskAbsenceOutgoingList = (
  absenceId: number | string,
  query: Query
): Promise<PaginationList<Task>> => {
  return sendRequest({
    name: 'taskAbsenceOutgoingList',
    method: 'get',
    url: {
      url: `/json/task/outgoingTasks/${absenceId}`,
      query,
    },
  })
}

const taskCreate = (credentials: TaskCredentials): Promise<Task> => {
  return sendRequest({
    name: 'taskCreate',
    method: 'post',
    url: '/json/task/create',
    payload: credentials,
  })
}

const taskSingle = (
  taskId: number | string,
  absenceId?: number | string | null
): Promise<Task> => {
  return sendRequest({
    name: 'taskSingle',
    method: 'get',
    url: withAbsence(`/json/task/single/${taskId}`, absenceId),
  })
}

const taskUpdate = (
  taskId: number | string,
  credentials: Partial<TaskCredentials>
): Promise<Task> => {
  return sendRequest({
    name: 'taskUpdate',
    method: 'post',
    url: `/json/task/update/${taskId}`,
    payload: credentials,
  })
}

const taskUpdateExecutor = (
  taskId: number | string,
  executorId: number | string,
  absenceId?: number | string | null
): Promise<Task> => {
  return sendRequest({
    name: 'taskUpdateExecutor',
    method: 'post',
    url: withAbsence(`/json/task/executor/${taskId}`, absenceId),
    payload: { executorId },
  })
}

const taskUpdateDeadline = (
  taskId: number | string,
  deadline: string,
  absenceId?: number | string | null
): Promise<Task> => {
  return sendRequest({
    name: 'taskUpdateDeadline',
    method: 'post',
    url: withAbsence(`/json/task/deadline/${taskId}`, absenceId),
    payload: { deadline },
  })
}

const taskTakeToWork = (
  taskId: number | string,
  absenceId?: number | string | null
): Promise<Task> => {
  return sendRequest({
    name: 'taskTakeToWork',
    method: 'get',
    url: withAbsence(`/json/task/takeToWork/${taskId}`, absenceId),
  })
}

const taskComplete = (
  taskId: number | string,
  completionReport: string,
  absenceId?: number | string | null
): Promise<Task> => {
  return sendRequest({
    name: 'taskComplete',
    method: 'post',
    url: withAbsence(`/json/task/complete/${taskId}`, absenceId),
    payload: { completionReport },
  })
}

const taskFinalComplete = (
  taskId: number | string,
  absenceId?: number | string | null
): Promise<Task> => {
  return sendRequest({
    name: 'taskFinalComplete',
    method: 'get',
    url: withAbsence(`/json/task/finalComplete/${taskId}`, absenceId),
  })
}

const taskReject = (
  taskId: number | string,
  rejectComment: string,
  absenceId?: number | string | null
): Promise<Task> => {
  return sendRequest({
    name: 'taskReject',
    method: 'post',
    url: withAbsence(`/json/task/reject/${taskId}`, absenceId),
    payload: { rejectComment },
  })
}

const taskRevision = (
  taskId: number | string,
  absenceId?: number | string | null
): Promise<Task> => {
  return sendRequest({
    name: 'taskRevision',
    method: 'get',
    url: withAbsence(`/json/task/revision/${taskId}`, absenceId),
  })
}

const taskDelete = (taskId: number | string): Promise<void> => {
  return sendRequest({
    name: 'taskDelete',
    method: 'delete',
    url: `/json/task/delete/${taskId}`,
  })
}

const taskDelegate = (
  taskId: number | string,
  executorId: number | string
): Promise<Task> => {
  return sendRequest({
    name: 'taskDelegate',
    method: 'post',
    url: `/json/task/delegate/${taskId}`,
    payload: { executorId },
  })
}

const taskCopy = (
  taskId: number | string,
  executorId: number | string
): Promise<Task> => {
  return sendRequest({
    name: 'taskCopy',
    method: 'post',
    url: `/json/task/copy/${taskId}`,
    payload: { executorId },
  })
}

const taskControlList = (query: Query): Promise<PaginationList<Task>> => {
  return sendRequest({
    name: 'taskControlList',
    method: 'get',
    url: {
      url: '/json/task/control',
      query,
    },
  })
}

const taskObserverList = (query: Query): Promise<PaginationList<Task>> => {
  return sendRequest({
    name: 'taskObserverList',
    method: 'get',
    url: {
      url: '/json/task/observer',
      query,
    },
  })
}

const taskDashboard = (): Promise<TaskDashboardResponse> => {
  return sendRequest({
    name: 'taskDashboard',
    method: 'get',
    url: '/json/task/dashboard',
  })
}

const taskControlUserCreate = (credentials: {
  taskId: number | string
  userId: number | string
}): Promise<TaskControlUser> => {
  return sendRequest({
    name: 'taskControlUserCreate',
    method: 'post',
    url: '/json/task/controlUser/create',
    payload: credentials,
  })
}

const taskControlUserDelete = (
  controlUserId: number | string,
  absenceId?: number | string | null
): Promise<void> => {
  return sendRequest({
    name: 'taskControlUserDelete',
    method: 'delete',
    url: withAbsence(
      `/json/task/controlUser/delete/${controlUserId}`,
      absenceId
    ),
  })
}

const taskObserverUserCreate = (credentials: {
  taskId: number | string
  userId: number | string
}): Promise<TaskObserverUser> => {
  return sendRequest({
    name: 'taskObserverUserCreate',
    method: 'post',
    url: '/json/task/observers/create',
    payload: credentials,
  })
}

const taskObserverUserDelete = (
  observerId: number | string,
  absenceId?: number | string | null
): Promise<void> => {
  return sendRequest({
    name: 'taskObserverUserDelete',
    method: 'delete',
    url: withAbsence(`/json/task/observers/delete/${observerId}`, absenceId),
  })
}

const taskPrintedForm = (
  type: TaskPrintedFormType
): Promise<TaskPrintedFormResponse> => {
  return sendRequest({
    name: 'taskPrintedForm',
    method: 'get',
    url: `/json/task/listPrintedForm?type=${type}`,
  })
}

const taskRelatedCreate = (
  taskId: number | string,
  relatedId: number | string
): Promise<Task> => {
  return sendRequest({
    name: 'taskRelatedCreate',
    method: 'post',
    url: `/json/task/related/${taskId}`,
    payload: { relatedId },
  })
}

const taskRelatedDelete = (
  taskId: number | string,
  relatedId: number | string
): Promise<Task> => {
  return sendRequest({
    name: 'taskRelatedDelete',
    method: 'post',
    url: `/json/task/unrelated/${taskId}`,
    payload: { relatedId },
  })
}

export {
  taskList,
  taskIncomingList,
  taskAbsenceIncomingList,
  taskOutgoingList,
  taskAbsenceOutgoingList,
  taskCreate,
  taskSingle,
  taskUpdate,
  taskUpdateExecutor,
  taskUpdateDeadline,
  taskTakeToWork,
  taskComplete,
  taskFinalComplete,
  taskReject,
  taskRevision,
  taskDelete,
  taskDelegate,
  taskCopy,
  taskControlList,
  taskObserverList,
  taskDashboard,
  taskControlUserCreate,
  taskControlUserDelete,
  taskObserverUserCreate,
  taskObserverUserDelete,
  taskPrintedForm,
  taskRelatedCreate,
  taskRelatedDelete,
}
